import { useState, useEffect } from "react";

export function useMaintenanceData(supabase, landlordId, session, reload) {
  const [maintenance, setMaintenance] = useState([]);
  const [showAddMaint, setShowAddMaint] = useState(false);
  const [newMaint, setNewMaint] = useState({ property: "", tenant: "", issue: "", category: "Plumbing", priority: "medium", notes: "", status: "open", contractor: "", cost: "", follow_up: false, date_reported: new Date().toISOString().slice(0,10), date_resolved: "" });
  const [showResolveMaint, setShowResolveMaint] = useState(null);
  const [resolveDate, setResolveDate] = useState("");
  const [editMaintMode, setEditMaintMode] = useState(false);
  const [editMaintData, setEditMaintData] = useState(null);
  const [viewMaint, setViewMaint] = useState(null);
  const [maintNotes, setMaintNotes] = useState([]);
  const [maintNoteInput, setMaintNoteInput] = useState("");
  const [postingMaintNote, setPostingMaintNote] = useState(false);

  useEffect(() => {
    if (!viewMaint?.id) { setMaintNotes([]); setMaintNoteInput(""); return; }
    supabase.from("maintenance_notes")
      .select("*")
      .eq("maintenance_id", viewMaint.id)
      .order("created_at", { ascending: false })
      .then(({ data }) => setMaintNotes(data || []));
  }, [viewMaint?.id]);

  const addMaintenance = async () => {
    if (!newMaint.issue) return;
    const payload = {
      property: newMaint.property || null, tenant: newMaint.tenant || null,
      issue: newMaint.issue, category: newMaint.category,
      priority: newMaint.priority, status: newMaint.status,
      notes: newMaint.notes || null, contractor: newMaint.contractor || null,
      cost: newMaint.cost ? Math.round(parseFloat(newMaint.cost)) : null,
      follow_up: newMaint.follow_up || false,
      date: newMaint.date_reported || new Date().toISOString().slice(0, 10),
      resolved_date: newMaint.date_resolved || null,
    };
    setShowAddMaint(false);
    const { error } = await supabase.from("maintenance").insert([{ ...payload, user_id: landlordId }]);
    if (error) { alert("Error: " + error.message); return; }
    supabase.from("notifications").insert([{
      user_id: landlordId, type: "maintenance",
      message: `${payload.tenant || "A tenant"} submitted a maintenance request: ${payload.issue}`,
      link: "Maintenance",
    }]).then(({ error: nErr }) => { if (nErr) console.warn("[notifications] maintenance insert failed:", nErr.message); });
    setNewMaint({ property: "", tenant: "", issue: "", category: "Plumbing", priority: "medium", notes: "", status: "open", contractor: "", cost: "", follow_up: false, date_reported: new Date().toISOString().slice(0,10), date_resolved: "" });
    reload();
  };

  const deleteMaintenance = async (id) => {
    if (!window.confirm("Delete this maintenance request?")) return;
    await supabase.from("maintenance").delete().eq("id", id);
    reload();
  };

  const updateMaintFull = async (id, updates) => {
    await supabase.from("maintenance").update(updates).eq("id", id);
    reload();
  };

  const createMaintenanceExpense = async (maint) => {
    const cost = Number(maint.cost);
    if (!cost || cost <= 0 || maint.status !== "resolved") return;
    await supabase.from("expenses").insert([{
      date: maint.resolved_date || new Date().toISOString().slice(0, 10),
      category: "Maintenance",
      description: `${maint.issue || "Maintenance"} — ${maint.property || ""}`.trim(),
      amount: Math.round(cost),
      property: maint.property || null,
      user_id: landlordId,
    }]);
  };

  const saveMaintenanceEdit = async () => {
    if (!viewMaint || !editMaintData) return;
    const ed = editMaintData;
    const updates = {
      issue: ed.issue, category: ed.category, priority: ed.priority, status: ed.status,
      contractor: ed.contractor || null, cost: ed.cost ? Math.round(parseFloat(ed.cost)) : null,
      notes: ed.notes || null, date: ed.date || null,
      resolved_date: ed.resolved_date || null, follow_up: ed.follow_up || false,
    };
    await updateMaintFull(viewMaint.id, updates);
    if (updates.status === "resolved" && viewMaint.status !== "resolved") {
      await createMaintenanceExpense({ ...viewMaint, ...updates });
    }
    const now = new Date();
    const loggedBy = session?.user?.email || "admin";
    await supabase.from("communications").insert([{
      tenant: viewMaint.tenant || null, type: "Maintenance Related",
      subject: `Maintenance ticket edited by ${loggedBy}`,
      body: `Ticket: "${ed.issue}" (${ed.category} · ${ed.priority} priority · ${ed.status.replace("_", " ")}) was edited on ${now.toLocaleDateString()} at ${now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}.`,
      date: now.toISOString().slice(0, 10), user_id: landlordId, priority: "informational", logged_by: loggedBy,
    }]);
    setViewMaint(prev => ({ ...prev, ...updates }));
    setEditMaintMode(false);
    setEditMaintData(null);
    reload();
  };

  const updateMaintStatus = async (id, status) => {
    await supabase.from("maintenance").update({ status }).eq("id", id);
    if (status === "resolved") {
      const { data: row } = await supabase.from("maintenance").select("issue, property, cost, resolved_date, status").eq("id", id).maybeSingle();
      if (row) await createMaintenanceExpense(row);
    }
    reload();
  };

  const postMaintNote = async () => {
    const text = maintNoteInput.trim();
    if (!text || !viewMaint?.id) return;
    setPostingMaintNote(true);
    const { data, error } = await supabase.from("maintenance_notes").insert([{
      maintenance_id: viewMaint.id,
      user_id: session?.user?.id,
      note: text,
    }]).select().single();
    if (error) { alert("Error saving note: " + error.message); setPostingMaintNote(false); return; }
    setMaintNotes(prev => [data, ...prev]);
    setMaintNoteInput("");
    setPostingMaintNote(false);
  };

  return {
    maintenance, setMaintenance,
    showAddMaint, setShowAddMaint,
    newMaint, setNewMaint,
    viewMaint, setViewMaint,
    editMaintMode, setEditMaintMode,
    editMaintData, setEditMaintData,
    showResolveMaint, setShowResolveMaint,
    resolveDate, setResolveDate,
    maintNotes, maintNoteInput, setMaintNoteInput, postingMaintNote, postMaintNote,
    addMaintenance, deleteMaintenance, updateMaintFull, saveMaintenanceEdit, updateMaintStatus,
  };
}
